import { Route } from 'react-resource-router';

import { Apps } from '@townsquare/app-context';
import { getConfig } from '@townsquare/config';
import { fg } from '@townsquare/stat-sig/gate';

import type {
	AccessControlCapability,
	CapabilityContainer,
} from './CapabilityRouter/__generated__/CapabilityRouterFragment.graphql';
import type { RouteWithOutlet, RouteWithSubproduct, UserCapabilityType } from './types';

export function isRouteWithOutlet(route: Route): route is RouteWithOutlet {
	return Object.prototype.hasOwnProperty.call(route, 'type');
}

export function isRouteWithSubproduct(route: Route): route is RouteWithSubproduct {
	return Object.prototype.hasOwnProperty.call(route, 'subproduct');
}

export const getRoleFromCapability = (
	capabilityContainer: CapabilityContainer,
	capability: AccessControlCapability,
): UserCapabilityType => {
	return `${capabilityContainer.toLowerCase()}:${capability.toLowerCase()}` as UserCapabilityType;
};

export const ORG_SITE_ROUTE_REGEX = new RegExp(
	`^/o/(?<orgIdentifier>[0-9a-zA-Z-]+)(/s/(?<siteIdentifier>[a-zA-Z0-9%+-]+))*`,
);

// Strips the URL to get route path
// eg. `https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals/{extra}` to `/goals/{extra}`
export const getRoutePathFromUrl = (url: string) => {
	return url.replace(getConfig().fullUrl, '').replace(ORG_SITE_ROUTE_REGEX, '');
};

export const isGoalsAppRoute = (url: string) => {
	const path = getRoutePathFromUrl(url);
	return (
		path.startsWith('/goal') ||
		path.startsWith('/agg-goal') ||
		(fg('townsquare-search-action-correct-app') && path === '/action/search/goals')
	);
};

export const isProjectsAppRoute = (url: string) => {
	if (!fg('atlas_projects_app_enabled')) {
		return false;
	}

	const path = getRoutePathFromUrl(url);
	return (
		path.startsWith('/project') || (fg('townsquare-search-action-correct-app') && path === '/action/search/projects')
	);
};

export const isTeamsAppRoute = (url: string) => {
	const path = getRoutePathFromUrl(url);
	return (
		path.startsWith('/people') ||
		(fg('townsquare-search-action-correct-app') &&
			(path === '/action/search/teams' || path === '/action/search/people'))
	);
};

export const isStudioAppRoute = (url: string) => {
	const path = getRoutePathFromUrl(url);
	return path.startsWith('/studio');
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @param app App ie. Goals, Teams, Home
 * @returns if route belongs to app
 */
export const isRouteOfApp = (route: string, app: Apps) => {
	if (isGoalsAppRoute(route)) {
		return app === 'goals';
	}

	if (isProjectsAppRoute(route)) {
		return app === 'projects';
	}

	if (isTeamsAppRoute(route)) {
		return app === 'teams';
	}

	// Legacy studio routes do not belong to any Townsquare app
	if (fg('enable-spa-string-hrefs-in-router-link-component') && isStudioAppRoute(route)) {
		return false;
	}

	return app === 'home';
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @param app App ie. Goals, Teams, Home
 * @returns if route belongs to an app different from the one provided
 */
export const isRouteOfOtherApp = (route: string, app: Apps) => {
	if (isRouteOfApp(route, app)) {
		return false;
	}

	if (app !== 'goals' && isGoalsAppRoute(route)) {
		return true;
	}

	if (app !== 'projects' && isProjectsAppRoute(route)) {
		return true;
	}

	if (app !== 'teams' && isTeamsAppRoute(route)) {
		return true;
	}

	return false;
};
